/**
 * ProtectedRoute - Route Protection Component
 *
 * Authenticated olmayan kullanıcıları login sayfasına yönlendirir.
 * Role bazlı + ekran-bazlı koruma sağlar.
 *
 * Props:
 *   - requiredRole: 'admin' | 'agent' | 'warehouse' | 'shipping'
 *     Admin her zaman bypass eder.
 *   - loginPath: Ziel ohne Anmeldung (Kundenportal: '/kunde/login')
 *   - requiredScreen: SCREENS registry key (örn. 'AdminDashboard')
 *     Verilirse user.permissions[screen] === true olmalı.
 *     Admin için her zaman true.
 */

import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/api/AuthContext';
import { Loader2, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ProtectedRoute({ children, requiredRole, requiredScreen, loginPath = '/' }) {
    const { user, loading, isAuthenticated, canAccess } = useAuth();

    // Auth yükleniyor
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-900" />
                    <p className="text-sm text-slate-500">Laden...</p>
                </div>
            </div>
        );
    }

    // Giriş yapılmamışsa unified login (Home) sayfasına yönlendir
    if (!isAuthenticated) {
        return <Navigate to={loginPath} replace />;
    }

    // Rol kontrolü - admin tüm alanlara erişebilir (süper kullanıcı bypass)
    if (requiredRole && user.role !== 'admin' && user.role !== requiredRole.toLowerCase()) {
        return <Navigate to="/" replace />;
    }

    // Ekran-bazlı izin kontrolü (rol kontrolünün üstüne)
    if (requiredScreen && !canAccess(requiredScreen)) {
        return <AccessDenied screenName={requiredScreen} />;
    }

    return children;
}

function AccessDenied({ screenName }) {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
            <div className="max-w-md text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <ShieldOff className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-xl font-bold text-slate-900 mb-2">Kein Zugriff</h1>
                <p className="text-sm text-slate-600 mb-6">
                    Sie haben keine Berechtigung für diesen Bereich. Bitte wenden Sie sich an
                    Ihren Administrator, falls Sie Zugriff benötigen.
                </p>
                <p className="text-xs text-slate-400 mb-6 font-mono">{screenName}</p>
                <Button onClick={() => navigate(-1)} variant="outline" className="min-h-[44px]">
                    Zurück
                </Button>
            </div>
        </div>
    );
}
